// `extension-alert-edit.tpl.html:1` plus the container chain the edit state renders inside.
//
// ⚠️ THE EDIT SCREEN IS THE FULL VIEW MODE, AND THAT IS ITS WHOLE LAYOUT. The review screen is
// a 200px panel with a side view; this one is `shortlist-content-container-FULL`, which puts a
// 220px rail on the left (`.shortlist-tabs-container`) holding the alert tab in its EDIT state
// and the criteria navigation, and scrolls the form beside it. Rendering the criteria builder
// on its own — which is what it does standing alone, and looks complete — loses the rail, the
// match prediction, Save and Cancel, and the alert's own name.
//
// The chain between the rail and the form is four elements deep and each one is load-bearing
// somewhere in the cascade: `.shortlist-item.shortlist-item-full-view` carries the
// `min-width: $chrome-extension-search-form-width` that stops the form collapsing,
// `<extension-alert-container>` and `.extension-alert-container` are `display: flex; flex: 1`,
// and `.extension-alert-edit-container` is the form's own root. Measured against the live
// tenant rather than read off the template.
import {
  createElement,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react'

export interface AlertEditFrameProps {
  /** `editedExtensionAlert.name` — shown as text, and EDITABLE ON CLICK. */
  alertName?: string
  /**
   * `editedExtensionAlert.id === NEW_ALERT_ID` — a search that has never been saved.
   *
   * ⚠️ A NEW SEARCH OPENS WITH THE NAME EDITOR ALREADY OPEN AND FOCUSED, which this port did
   * not do: `initEditedExtensionAlert` runs `$timeout(() => this.onEditName(true), 1000)` for
   * exactly this case, and selects the text when the name is still the blank default. Without
   * it the screen offers no way to name a search except noticing that the title is clickable —
   * and on a new one there is no title to notice, just an empty space where production shows
   * "Enter a hunter name".
   */
  newAlert?: boolean
  /**
   * `ng-model="ctrl.editedExtensionAlert.name"` on the rename input.
   *
   * ⚠️ WITHOUT THIS THE SCREEN PROMISED A RENAME IT COULD NOT DO. The span has
   * `title="Rename"`, so the browser shows that word on hover — over text that did nothing,
   * because the `ng-click` beside it (`extension-alert-edit.tpl.html:5`) and the input it
   * reveals were both missing. Found by the event-binding parity check, not by looking.
   */
  onAlertNameChange?: (name: string) => void
  /**
   * `getJobAutocomplete` — each row carries the job AND the string the row shows, because
   * that string is built from two whitelabel flags (`atsHasJobCode`,
   * `atsHasConcreteEmployers`) the frame has no business knowing about.
   */
  linkedJobSuggestions?: ReadonlyArray<{ id: number; autocompleteText: string }>
  onLinkedJobSelect?: (job: { id: number; autocompleteText: string }) => void
  /**
   * `featureFlags.LINKED_JOB_ENABLED && (!extensionProduct || extensionProduct === Candidates)`.
   * ⚠️ BOTH HALVES. The client-contact builder never shows it even where the flag is on.
   */
  linkedJobEnabled?: boolean
  /** `ctrl.linkedJobText` — the typeahead's current text. */
  linkedJobText?: string
  onLinkedJobTextChange?: (text: string) => void
  /** The alert tab, in its EDIT state — inside `.my-hunters-list > .shortlist-tab.selected`. */
  tab?: ReactNode
  /**
   * The criteria navigation.
   *
   * ⚠️ A SIBLING OF `.my-hunters-list`, NOT A CHILD OF THE TAB. Upstream closes the hunters
   * list before opening `.extension-criteria-navigation-container`
   * (shortlist-extension-alerts.tpl.html:126), and `.shortlist-tab` sets
   * `line-height: 29px` — so nesting the nav inside it stretches all nine rows and the rail
   * grows by 230px. Two elements that look interchangeable because they sit next to each other.
   */
  navigation?: ReactNode
  /**
   * `edit-alert-footer-container` — the Save / Cancel row at the FOOT of the form.
   *
   * ⚠️ IT IS A SECOND PAIR, NOT THE TAB'S. `extension-alert-edit.tpl.html:62` closes the
   * form with its own Save and Cancel, below every section; the pair in the alert tab
   * (`extension-alert-tab-save`) sits at the TOP of the rail and is the one this port had.
   * On a form 2,870px tall that is the difference between finishing the edit where you are
   * and scrolling back to the top to find the button — measured against build 354, which has
   * both at y=200 and y=2862.
   */
  onSave?: () => void
  onCancel?: () => void
  /** `ng-show="!ctrl.isFormValid"` — the message under the footer buttons. */
  formInvalid?: boolean
  children?: ReactNode
}

export function AlertEditFrame({
  alertName,
  newAlert = false,
  onAlertNameChange,
  linkedJobSuggestions = [],
  onLinkedJobSelect,
  linkedJobEnabled = false,
  linkedJobText = '',
  onLinkedJobTextChange,
  tab,
  onSave,
  onCancel,
  formInvalid = false,
  navigation,
  children,
}: AlertEditFrameProps): ReactElement {
  const [jobsOpen, setJobsOpen] = useState(false)
  /** `uib-typeahead`'s `activeIdx` — the row Enter would take, and the one marked `.active`. */
  const [activeJob, setActiveJob] = useState(0)
  /** `ctrl.editName` — which half of the name control is on screen. */
  const [editingName, setEditingName] = useState(false)
  const nameInput = useRef<HTMLInputElement>(null)
  const mirror = useRef<HTMLDivElement>(null)

  /**
   * `resizeNameInput()` + `utils.setFocus(...)`, which upstream runs inside `onEditName`.
   *
   * ⚠️ A LAYOUT EFFECT, NOT AN EFFECT, for upstream's own reason stated the other way round:
   * its `$timeout(…, 0)` exists because "width is based on a div in view, so we want the view
   * to refresh with new content first". Measuring after paint would show the user one frame of
   * a default-width input before it snaps.
   */
  /**
   * ⚠️ THE ONE-SECOND WAIT IS UPSTREAM'S, AND IT IS A WORKAROUND IT DOCUMENTS: "When loading
   * the page with an existing value, the resizing of the input value does not work as
   * expected… Trial and error shows that it takes at least a second to work." The mirror div
   * has to have laid out before its width can size the input, and on a cold screen that is
   * not the first frame.
   */
  useEffect(() => {
    if (!newAlert) return undefined
    const timer = setTimeout(() => {
      setEditingName(true)
    }, 1000)
    return () => {
      clearTimeout(timer)
    }
  }, [newAlert])

  useLayoutEffect(() => {
    if (!editingName) return
    const width = mirror.current?.getBoundingClientRect().width
    if (width !== undefined && nameInput.current !== null) {
      nameInput.current.style.width = `${String(Math.round(width) + 15)}px`
    }
    if (document.activeElement !== nameInput.current) {
      nameInput.current?.focus()
      // `$('input[name="alertName"]').select()` — only for a new alert still carrying the
      // blank default, so re-opening the editor on a named search does not wipe it on the
      // next keystroke.
      if (newAlert && (alertName ?? '') === '') nameInput.current?.select()
    }
  }, [editingName, alertName, newAlert])

  return (
    <div className="shortlist-content-container shortlist-content-container-full">
      <div className="shortlist-tabs-container">
        <div className="my-hunters-list">
          {/* `.selected` twice, nested — the outer is the tab, the inner is upstream's own
              `<div class="selected">` wrapper, and the stylesheet targets the inner one. */}
          <div className="shortlist-tab selected">
            <div className="selected">{tab === undefined ? null : tab}</div>
          </div>
        </div>
        {navigation === undefined ? null : navigation}
      </div>
      <div className="shortlist-item nicer-scroller mac-style shortlist-item-full-view">
        {createElement(
          'extension-alert-container',
          null,
          <div className="extension-alert-container">
            {/* An anonymous div in upstream's own template — `ng-scope` in the rendered DOM,
                no class. It is a flex child of the container and removing it re-parents the
                form. */}
            <div>
              {createElement(
                'extension-alert-edit',
                null,
                <div className="extension-alert-edit-container">
                  <div className="alert-edit-main-form">
                    <div className="edit-alert-header">
                      <div className="edit-alert-name">
                        {/* `ng-show="!ctrl.editName"` — the span and the input are the two
                            halves of one control, and upstream toggles between them. */}
                        {editingName ? null : (
                          <span
                            className="edit-alert-name-display"
                            title="Rename"
                            role="button"
                            tabIndex={0}
                            onClick={() => {
                              setEditingName(true)
                            }}
                            onKeyDown={(event) => {
                              if (event.key !== 'Enter' && event.key !== ' ') return
                              event.preventDefault()
                              setEditingName(true)
                            }}
                          >
                            {alertName}
                          </span>
                        )}
                        {/* ⚠️ THE MIRROR IS WHAT SIZES THE INPUT, and it is why this div is
                            here at all. `resizeNameInput()` reads `$('#editedName').width()`
                            and sets the input to that plus 15 — "we need some room for mouse
                            click after last character". A text input has no intrinsic width,
                            so without the mirror the field is a fixed box that either clips a
                            long name or leaves a gap after a short one. */}
                        <div className="hidden-name" id="editedName" ref={mirror}>
                          {alertName}
                        </div>
                        {editingName ? (
                          <input
                            ref={nameInput}
                            type="text"
                            name="alertName"
                            placeholder="Enter a hunter name"
                            // ⚠️ `ng-invalid-required` IS LOAD-BEARING, NOT A FRAMEWORK ARTEFACT.
                            // `_extension-alert-edit.scss:63` gives the input
                            // `min-width: 200px` under exactly that class, and AngularJS puts
                            // it there whenever a `required` field is empty — which is every
                            // new search. Without it the width comes only from the mirror div,
                            // which is also empty, so the field collapsed to the 15px of
                            // breathing room `resizeNameInput` adds and showed a bare cursor
                            // where production shows "Enter a hunter name".
                            className={`edit-alert-name-input extension-alert-edit-placeholder${
                              (alertName ?? '') === '' ? ' ng-invalid-required' : ''
                            }`}
                            value={alertName ?? ''}
                            required
                            onChange={(event) => {
                              onAlertNameChange?.(event.target.value)
                            }}
                            // `ng-blur="ctrl.onEditNameExit()"`, which returns early when the
                            // field is invalid — `required`, so an empty name keeps the editor
                            // open rather than committing a nameless alert.
                            onBlur={() => {
                              if ((alertName ?? '') === '') return
                              setEditingName(false)
                            }}
                            // `ui-keypress="{ 13: 'ctrl.onEditNameExit()' }"`.
                            onKeyDown={(event) => {
                              if (event.key !== 'Enter') return
                              event.preventDefault()
                              if ((alertName ?? '') === '') return
                              setEditingName(false)
                            }}
                          />
                        ) : null}
                      </div>
                      {linkedJobEnabled ? (
                        <div className="edit-alert-linked-job">
                          <label className="bold-soft linked-jobs-title">Linked job</label>
                          <label className="jobs-hourly-basis-label">Jobs are being updated on an hourly basis</label>
                          {/* ⚠️ `uib-typeahead`, NOT A TAGS-INPUT — a plain field with a
                              dropdown, whose rows come from
                              `shortlistExtensionJobService.getJobAutocomplete($viewValue)` and
                              render `extension-job-autocomplete.tpl.html`: an `<a>` wrapping
                              `.add-to-ats-job-autocomplete-text`, titled with the same string.
                              The note that stood here said the list "arrives with the
                              job-linking flow"; it never did, so the field looked like a
                              search box that searched nothing.

                              `typeahead-editable="false"` is the rule that matters: typed text
                              is NOT a value. Only a picked row sets the linked job, which is
                              why `onLinkedJobSelect` is separate from the text change. */}
                          <input
                            className="form-control input extension-alert-edit-placeholder"
                            type="text"
                            placeholder="Search for a job"
                            autoComplete="off"
                            value={linkedJobText}
                            onFocus={() => setJobsOpen(true)}
                            onBlur={() => setJobsOpen(false)}
                            onChange={(event) => {
                              setActiveJob(0)
                              onLinkedJobTextChange?.(event.target.value)
                            }}
                            // ⚠️ `uib-typeahead` BINDS THE WHOLE KEYBOARD AND THIS BOUND NONE
                            // OF IT. The runtime event sweep found a `keydown` listener on
                            // production's input against nothing here: the list opened, and
                            // the only way to take a row was the mouse. Arrow keys move the
                            // highlight, Enter takes it, Escape closes — and the highlight is
                            // the `.active` class the library puts on the row, which is what
                            // makes "which one will Enter take" visible at all.
                            onKeyDown={(event) => {
                              if (!jobsOpen || linkedJobSuggestions.length === 0) return
                              if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                                event.preventDefault()
                                setActiveJob((at) =>
                                  event.key === 'ArrowDown'
                                    ? (at + 1) % linkedJobSuggestions.length
                                    : (at - 1 + linkedJobSuggestions.length) % linkedJobSuggestions.length,
                                )
                                return
                              }
                              if (event.key === 'Enter') {
                                const job = linkedJobSuggestions[activeJob]
                                if (job === undefined) return
                                event.preventDefault()
                                setJobsOpen(false)
                                onLinkedJobSelect?.(job)
                                return
                              }
                              if (event.key === 'Escape') setJobsOpen(false)
                            }}
                          />
                          {jobsOpen && linkedJobSuggestions.length > 0 ? (
                            <ul className="dropdown-menu" role="listbox" style={{ display: 'block' }}>
                              {linkedJobSuggestions.map((job, index) => (
                                <li
                                  key={job.id}
                                  role="option"
                                  className={index === activeJob ? 'active' : undefined}
                                  aria-selected={index === activeJob}
                                  onMouseEnter={() => {
                                    setActiveJob(index)
                                  }}
                                >
                                  {/* `mousedown`, not `click`: blur closes the list first. */}
                                  <a
                                    tabIndex={-1}
                                    onMouseDown={(event) => {
                                      event.preventDefault()
                                      setJobsOpen(false)
                                      onLinkedJobSelect?.(job)
                                    }}
                                  >
                                    <div className="add-to-ats-job-autocomplete-text" title={job.autocompleteText}>
                                      {job.autocompleteText}
                                    </div>
                                  </a>
                                </li>
                              ))}
                            </ul>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                    {children}
                  </div>
                  {/* Outside `.alert-edit-main-form`, as upstream has it: the form scrolls and
                      the footer is the last thing in the edit container. */}
                  {onSave === undefined && onCancel === undefined ? null : (
                    <div className="edit-alert-footer-container">
                      <div className="edit-alert-footer">
                        <div className="edit-alert-button">
                          <button type="button" onClick={onSave}>
                            Save
                          </button>
                        </div>
                        <div className="edit-alert-cancel">
                          <a
                            href=""
                            onClick={(event) => {
                              event.preventDefault()
                              onCancel?.()
                            }}
                          >
                            Cancel
                          </a>
                        </div>
                      </div>
                      {formInvalid ? (
                        <label className="control-label has-error mB10">Form is empty or invalid</label>
                      ) : null}
                    </div>
                  )}
                </div>,
              )}
            </div>
          </div>,
        )}
      </div>
    </div>
  )
}
